import type {
  Command,
  MatchConnection,
  MatchLifecycleEvent,
  PlayerId,
  PlayerView,
  Unsubscribe,
} from '@signalclash/shared';
import * as Colyseus from 'colyseus.js';

// V2 client-side connection. Implements `MatchConnection` over a Colyseus
// `Room`. The wire format (per docs/08_MULTIPLAYER.md):
//
//   - Commands  : `command` message (client → server, raw Command shape).
//   - Snapshots : `state`   message (server → client, fogged PlayerView
//                   already projected by ColyseusMatchHost).
//   - Lifecycle : `lifecycle` message (server → client, MatchLifecycleEvent).
//
// We deliberately avoid Colyseus state-sync for the snapshot — V2's per-
// recipient fog projection means each client's view is already different,
// and broadcasting the full state then filtering client-side would leak
// invisible entities (the Rule 5 / docs/08_MULTIPLAYER.md anti-cheat
// boundary). A future PR can switch to Schema delta encoding for payload
// efficiency once the V2 spine is validated.
//
// Connection model:
//   - `connect(playerId)` calls `client.joinOrCreate('signal_clash', { room })`.
//     The optional `roomCode` constructor argument enables private rooms via
//     `?room=ABCD`. The first joiner becomes PLAYER_ZERO; we still pass
//     `playerId` so a future reconnect path can re-bind to a slot.
//   - The same `MatchConnection` interface as `LocalMatchConnection` —
//     ClientApp swaps implementations without touching the renderer / input
//     layers.

export interface ColyseusMatchConnectionOptions {
  endpoint: string;          // ws://host:port or wss://...
  roomCode?: string;         // ?room=ABCD; absent = quick-create
  roomType?: string;         // defaults to 'signal_clash'
}

// V2.5+ Reconnect-Bot: per-client join option. Threaded into
// `joinOrCreate(roomType, joinOptions)` (NOT into the room's create
// options) so each client sets it independently. The room only spawns
// a Reconnect-Bot when BOTH clients opted in.
export interface ColyseusConnectOptions {
  allowBotReconnectFill?: boolean;
}

interface StateMessage {
  kind: 'state';
  tick: number;
  state: PlayerView;
}

interface LifecycleMessage {
  kind: 'lifecycle';
  event: MatchLifecycleEvent;
}

interface AssignedMessage {
  kind: 'assigned';
  playerId: 0 | 1;
}

// V1.8 — wire-level error event for the UI to react to. Distinguishes
// soft errors (server-side game logic / room.onError) from hard ones
// (transport close / WebSocket failure / captive-portal kills). The
// banner uses the kind to choose its copy + retry button visibility.
export interface ColyseusConnectionError {
  kind: 'transport' | 'protocol';
  code: number;
  message: string;
}

export class ColyseusMatchConnection implements MatchConnection {
  private readonly client: Colyseus.Client;
  private readonly opts: ColyseusMatchConnectionOptions;
  private room: Colyseus.Room | null = null;
  private snapshotHandlers = new Set<(s: PlayerView) => void>();
  private lifecycleHandlers = new Set<(e: MatchLifecycleEvent) => void>();
  private errorHandlers = new Set<(e: ColyseusConnectionError) => void>();
  private playerId: PlayerId | null = null;

  constructor(opts: ColyseusMatchConnectionOptions) {
    this.opts = opts;
    this.client = new Colyseus.Client(opts.endpoint);
  }

  async connect(_playerId: PlayerId, options: ColyseusConnectOptions = {}): Promise<void> {
    // The first arg is part of the shared `MatchConnection` interface
    // contract; for Colyseus we ignore it because the SERVER is the
    // authority on slot assignment. Two real clients passing the same
    // playerId would otherwise both claim slot 0 — that was the V2
    // "slot 0 already occupied" bug. We wait for the `assigned`
    // message instead and consumers read `getAssignedPlayerId()`.
    const roomType = this.opts.roomType ?? 'signal_clash';
    // joinOrCreate semantics: if a room with this code exists we join it;
    // otherwise the server creates one. This is the V2 private-room flow
    // — no separate "create" step required.
    const joinOptions: Record<string, unknown> = {};
    if (this.opts.roomCode !== undefined) joinOptions.room = this.opts.roomCode;
    // V2.5+ Reconnect-Bot opt-in. Per-client (NOT per-room): each
    // client carries its own flag. Only set when true so the wire
    // payload stays minimal for the default case.
    if (options.allowBotReconnectFill === true) {
      joinOptions.allowBotReconnectFill = true;
    }
    this.room = await this.client.joinOrCreate(roomType, joinOptions);
    this.room.onLeave((code) => {
      // V1.8 — clear local state then fall through to the error sink
      // below so callers see ONE transport event even if onLeave fires
      // before the explicit onError. The order matters: clear refs
      // first so any synchronous send() inside an error handler bails.
      this.room = null;
      this.playerId = null;
      if (code !== 1000 && code !== 1001) {
        this.fireError({
          kind: 'transport',
          code,
          message: `WebSocket closed (code ${code})`,
        });
      }
    });
    // Colyseus fires `onError` for protocol-level WS errors (transport
    // close with a non-1000 code, mismatched payload, etc.). Without an
    // explicit handler the error path is silent — the player sees a
    // frozen game and the portal sees a session that stopped sending
    // analytics mid-match. Log via the main.ts global error stream so
    // these surface in the dev console + portal sampling, while leaving
    // the silent freeze for the existing `room.onLeave` cleanup to
    // observe.
    this.room.onError((code, message) => {
      // eslint-disable-next-line no-console
      console.warn('[colyseus] room error', code, message);
      this.fireError({ kind: 'protocol', code, message: message ?? 'protocol error' });
    });
    // Wire all three message types BEFORE awaiting the assignment so
    // we don't race the server (the server emits `assigned`
    // immediately after `onJoin` resolves, before the first state
    // pump — but Colyseus's onMessage handlers are level-triggered
    // and we'd lose the message if not subscribed by the time it
    // arrives).
    // Error containment: a throw inside any one subscriber must not
    // (a) stop the rest of the subscribers for this tick, or
    // (b) propagate back to Colyseus's onMessage handler, which would
    //     drop the WebSocket and force a reconnect mid-match.
    // ~13 subscribers receive every state tick (Renderer, Sfx, Music,
    // HUD, BuildPanel, Minimap, KillEvents, EndOfMatch, EffectsLayer,
    // FloatingTextLayer, RelayUnderAttackAlert, DoctrinePicker,
    // Tutorial). One bad snapshot shape would otherwise cascade into
    // a mid-match disconnect that the portal counts as a crash.
    this.room.onMessage('state', (msg: StateMessage) => {
      for (const fn of this.snapshotHandlers) {
        try {
          fn(msg.state);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[snapshot handler] threw', err);
        }
      }
    });
    this.room.onMessage('lifecycle', (msg: LifecycleMessage) => {
      for (const fn of this.lifecycleHandlers) {
        try {
          fn(msg.event);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[lifecycle handler] threw', err);
        }
      }
    });
    // Wait for the slot-assignment message before resolving. The
    // server sends it synchronously in `onJoin`, so this typically
    // resolves on the next tick. We bound the wait so a server bug
    // doesn't hang the lobby UI forever.
    //
    // 15 s ceiling: matches the Quick Play bot-fill window so a
    // cold-started Fly.io machine (typical wake from scale-to-zero
    // is 5–10 s, occasionally longer on a busy region) has time to
    // hand the joining client its slot. The previous 5 s bound was
    // tripping legitimate joins on cold servers and surfacing them
    // as "Connection failed" — a session that never reached
    // gameplayStart counts as a failed Conversion on CrazyGames.
    this.playerId = await this.awaitAssigned(15_000);
  }

  // Subscribe to the `assigned` message and resolve the first one;
  // subsequent ones are ignored (there should be at most one per
  // join). Times out after `timeoutMs` and rejects so the caller can
  // surface the failure in the lobby UI.
  private awaitAssigned(timeoutMs: number): Promise<PlayerId> {
    return new Promise<PlayerId>((resolve, reject) => {
      if (this.room === null) {
        reject(new Error('room not joined'));
        return;
      }
      const room = this.room;
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('timeout waiting for slot assignment'));
      }, timeoutMs);
      room.onMessage('assigned', (msg: AssignedMessage) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(msg.playerId);
      });
    });
  }

  getAssignedPlayerId(): PlayerId | null {
    return this.playerId;
  }

  async disconnect(): Promise<void> {
    if (this.room !== null) {
      try {
        await this.room.leave();
      } finally {
        this.room = null;
      }
    }
    this.playerId = null;
  }

  send(command: Command): void {
    if (this.room === null) return;
    if (!this.room.connection.isOpen) return;
    this.room.send('command', command);
  }

  onSnapshot(handler: (snapshot: PlayerView) => void): Unsubscribe {
    this.snapshotHandlers.add(handler);
    return () => {
      this.snapshotHandlers.delete(handler);
    };
  }

  onLifecycle(handler: (event: MatchLifecycleEvent) => void): Unsubscribe {
    this.lifecycleHandlers.add(handler);
    return () => {
      this.lifecycleHandlers.delete(handler);
    };
  }

  // V1.8 — transport / protocol error stream. ClientApp subscribes here
  // and routes to ReconnectBanner so the player gets a manual retry
  // instead of a frozen frame.
  onError(handler: (error: ColyseusConnectionError) => void): Unsubscribe {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  private fireError(error: ColyseusConnectionError): void {
    for (const fn of this.errorHandlers) {
      try {
        fn(error);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[error handler] threw', err);
      }
    }
  }
}
